import { memo } from "react";

import { useAppDispatch } from "../../../../redux/hooks";
import {
  getDownloadNrcd,
  getRenumberProducts,
} from "../../../../redux/api/receipt.api";
import { TableItemType } from "../../../../components/tables/tableContext/TableContext";

import { TableItemId } from "../../../../components/tables/TableWidget";
import { receptionRoutes } from "../../../../router/routesConstants";
import { dateToString } from "../../../../utils/dateAndTime";
import ActionMenu, {
  ActionMenuItem,
} from "../../../../components/actionMenu/ActionMenu";

function ReceptionTableActionButtons({
  crudData,
}: {
  itemId: TableItemId;
  tableItemData: TableItemType[];
  crudData?: any;
  tableContext?: any;
}) {
  const dispatch = useAppDispatch();

  const itemStatusArr = crudData.statusZona
    .split(",")
    .map((item: any) => parseInt(item));

  const buttonNrcdEnabled = crudData?.buttonNrcdEnabled;
  const buttonQuarantineEnabled = crudData?.buttonQuarantineEnabled;
  const showProductRenumberingButton = itemStatusArr.includes(3);

  const buttonPreviewNrcdEnabled =
    itemStatusArr.includes(2) &&
    itemStatusArr.includes(3) &&
    itemStatusArr[itemStatusArr.length - 1] < 4;

  const receivedAt = dateToString(crudData?.createdAt);

  const handleEditReception = () => {
    return `/${receptionRoutes.reception}/${receptionRoutes.editReception}?idFurnizor=${crudData?.idFurnizor}&idFlux=${crudData?.idFluxReceptie}`;
  };

  const addInvoice = () => {
    return `/${receptionRoutes.reception}/${receptionRoutes.addInvoice}?idFurnizor=${crudData?.idFurnizor}&idFlux=${crudData?.idFluxReceptie}`;
  };

  const goToNrcd = () => {
    return `/${receptionRoutes.reception}/${receptionRoutes.nrcd}?reception=${crudData.idFluxReceptie}&createdAt=${crudData.createdAt}`;
  };

  const goToPreviewQuarantine = () => {
    return `/${receptionRoutes.reception}/${receptionRoutes.previewQuarantine}?reception=${crudData.idFluxReceptie}&createdAt=${crudData.createdAt}`;
  };

  const goToQuarantine = () => {
    return `/${receptionRoutes.reception}/${receptionRoutes.quarantine}?reception=${crudData.idFluxReceptie}&createdAt=${crudData.createdAt}`;
  };

  const goToProducts = () => {
    return `/${receptionRoutes.reception}/${receptionRoutes.receivedProducts}?reception=${crudData.idFluxReceptie}&data=${receivedAt}`;
  };

  const goToLabeledProducts = () => {
    return `/${receptionRoutes.reception}/${
      receptionRoutes.labeledProducts
    }?reception=${crudData.idFluxReceptie}&furnizor=${encodeURIComponent(
      crudData.numeFurnizor ?? "",
    )}&data=${receivedAt}`;
  };

  const handleDownloadNrcd = () => {
    dispatch(
      getDownloadNrcd({
        nrcdId: crudData.idFluxReceptie,
        furnizor: crudData.numeFurnizor,
        data: receivedAt,
      }),
    );

    return;
  };

  const handleRenumberingProducts = () => {
    const callBack = () => {
      // tableContext.setTablePagination((prev: any) => ({ ...prev, page: 1 }));
    };
    dispatch(
      getRenumberProducts({
        idFluxReceptie: crudData.idFluxReceptie,
        callBack,
      }),
    );
  };

  const items: ActionMenuItem[] = [
    {
      id: "received-products",
      label: "Produse receptionate",
      icon: "RECEIVED_PRODUCTS",
      tooltip: "Afiseaza produsele receptionate in aceasta receptie.",
      navigate: goToProducts(),
    },
    {
      id: "labeled-products",
      label: "Afiseaza marfa trimisa etichetat",
      icon: "LABEL",
      tooltip: "Afiseaza produsele trimise etichetat pentru aceasta receptie.",
      hidden: Number(crudData?.receptionType) !== 1,
      navigate: goToLabeledProducts(),
    },
    {
      id: "add-invoice",
      label: "Adauga factura",
      icon: "ADD_INVOICE",
      tooltip: "Deschide adaugarea unei facturi pentru aceasta receptie.",
      navigate: addInvoice(),
    },
    {
      id: "view-nir",
      dividerBefore: true,
      label: "Vizualizare NIR",
      icon: "NIR_VISUALIZATION",
      tooltip: buttonNrcdEnabled
        ? "Deschide NIR-ul acestei receptii."
        : "Vizualizarea NIR nu este disponibila pentru aceasta receptie.",
      disabled: !buttonNrcdEnabled,
      navigate: goToNrcd(),
    },
    {
      id: "preview-nonconformities",
      label: "Previzualizare neconformitati",
      icon: "PREVIEW_NONCONFORMITIES",
      tooltip: buttonPreviewNrcdEnabled
        ? "Deschide previzualizarea neconformitatilor acestei receptii."
        : "Previzualizarea neconformitatilor nu este disponibila in stadiul curent al receptiei.",
      disabled: !buttonPreviewNrcdEnabled,
      navigate: goToPreviewQuarantine(),
    },
    {
      id: "view-nonconformities",
      label: "Vezi neconformitati",
      icon: "PREVIEW_NONCONFORMITIES",
      tooltip: buttonQuarantineEnabled
        ? "Deschide neconformitatile acestei receptii."
        : "Vizualizarea neconformitatilor nu este disponibila pentru aceasta receptie.",
      disabled: !buttonQuarantineEnabled,
      navigate: goToQuarantine(),
    },
    {
      id: "download-nonconformities",
      label: "Descarca PV neconformitati",
      icon: "DOCUMENT_WITH_DOWNLOAD",
      kind: "download",
      tooltip:
        "Descarca PDF-ul procesului-verbal de neconformitati pentru aceasta receptie.",
      onClick: handleDownloadNrcd,
    },
    {
      id: "edit-reception",
      dividerBefore: true,
      label: "Editeaza receptia",
      icon: "EDIT",
      tooltip: "Deschide modificarea listei de facturi a receptiei.",
      navigate: handleEditReception(),
    },
    {
      id: "unlock-reception",
      label: "Deblocare receptie",
      icon: "LOCK_OPEN",
      tooltip:
        buttonNrcdEnabled || !showProductRenumberingButton
          ? "Deblocarea receptiei nu este disponibila in stadiul curent."
          : "Deblocheaza receptia pentru reluarea numararii produselor, dupa confirmare.",
      disabled: buttonNrcdEnabled || !showProductRenumberingButton,
      withConfirmationModal: {
        modalTitle: `Vreti sa incepeti renumararea produselor pentru ${crudData.numeFurnizor}?`,
        modalSubtitle: "Acest proces poate dura cateva minute",
        modalLeftButtonText: "Nu",
        modalRightButtonText: "Da",
        modalLeftButtonColor: "primary",
        modalRightButtonColor: "error",
        modalRightButtonOnClick: handleRenumberingProducts,
      },
    },
  ];

  return (
    <ActionMenu
      items={items}
      minItemsForMenu={2}
      title={crudData.numeFurnizor}
      ariaLabel={`Actiuni pentru receptia ${crudData.idFluxReceptie}`}
    />
  );
}

export default memo(ReceptionTableActionButtons);
